import { useCallback, useEffect, useState } from "react";
import { useDisclosure } from "@chakra-ui/hooks";
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from "@chakra-ui/modal";
import { Button, ChakraProvider } from "@chakra-ui/react";
import Loader from "components/loader/loader";
import Navbar from "../../../components/navbar";
import { t } from "i18next";
import { errorToast, successToast } from "utils/commons";
import {
    getAllRatingsAdminApi,
    rejectRatingAdminApi,
} from "../../../services/customAPI";
import { MdDelete, MdVisibility, MdFlag, MdStar } from "react-icons/md";

type FilterTab = "all" | "driver" | "rider" | "reported";

interface RatingRow {
    id: number;
    rideId: number;
    raterId: number;
    ratedId: number;
    raterType: "driver" | "rider";
    ratedType: "driver" | "rider";
    stars: number;
    comment?: string;
    isReported: boolean;
    reportReason?: string;
    reportComment?: string;
    createdAt: string;
    raterDriver?: { firstName?: string; lastName?: string };
    raterRider?: { firstName?: string; lastName?: string };
    ratedDriver?: { firstName?: string; lastName?: string };
    ratedRider?: { firstName?: string; lastName?: string };
    ride?: { id?: number };
}

const StarDisplay = ({ stars }: { stars: number }) => (
    <span className="inline-flex gap-0.5 text-base">
        {[1, 2, 3, 4, 5].map((s) => (
            <span key={s} style={{ color: s <= stars ? "#FFD700" : "#3A3A4A" }}>
                ★
            </span>
        ))}
    </span>
);

const getRaterName = (row: RatingRow): string => {
    if (row.raterType === "driver" && row.raterDriver) {
        return `${row.raterDriver.firstName || ""} ${row.raterDriver.lastName || ""}`.trim() || `Motorista #${row.raterId}`;
    }
    if (row.raterType === "rider" && row.raterRider) {
        return `${row.raterRider.firstName || ""} ${row.raterRider.lastName || ""}`.trim() || `Passageiro #${row.raterId}`;
    }
    return `#${row.raterId}`;
};

const getRatedName = (row: RatingRow): string => {
    if (row.ratedType === "driver" && row.ratedDriver) {
        return `${row.ratedDriver.firstName || ""} ${row.ratedDriver.lastName || ""}`.trim() || `Motorista #${row.ratedId}`;
    }
    if (row.ratedType === "rider" && row.ratedRider) {
        return `${row.ratedRider.firstName || ""} ${row.ratedRider.lastName || ""}`.trim() || `Passageiro #${row.ratedId}`;
    }
    return `#${row.ratedId}`;
};

const Ratings = () => {
    const { isOpen, onOpen, onClose: onDisclosureClose } = useDisclosure();

    const [loading, setLoading] = useState(false);
    const [ratings, setRatings] = useState<RatingRow[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [activeTab, setActiveTab] = useState<FilterTab>("all");

    const [selectedRating, setSelectedRating] = useState<RatingRow | null>(null);
    const [modalMode, setModalMode] = useState<"view" | "reject" | null>(null);
    const [rejecting, setRejecting] = useState(false);

    const onClose = () => {
        onDisclosureClose();
        setModalMode(null);
        setSelectedRating(null);
    };

    const fetchRatings = useCallback(
        async (pageNum: number = 1) => {
            try {
                setLoading(true);
                const filters: { raterType?: string; reported?: string } = {};
                if (activeTab === "driver") filters.raterType = "driver";
                if (activeTab === "rider") filters.raterType = "rider";
                if (activeTab === "reported") filters.reported = "true";

                const response = await getAllRatingsAdminApi(pageNum, 15, filters);
                const payload = response?.data ?? response;
                const ratingData = payload?.data ? payload.data : payload;
                if (ratingData) {
                    setRatings(ratingData.ratings || []);
                    setTotalPages(ratingData.totalPages || 1);
                    setTotalCount(ratingData.totalCount || 0);
                    setPage(pageNum);
                }
            } catch (error) {
                console.error("Error fetching ratings:", error);
                errorToast(t("Erro ao buscar avaliações"));
            } finally {
                setLoading(false);
            }
        },
        [activeTab],
    );

    useEffect(() => {
        fetchRatings(1);
    }, [fetchRatings]);

    const handleReject = async () => {
        if (!selectedRating?.id) return;
        try {
            setRejecting(true);
            await rejectRatingAdminApi(selectedRating.id);
            successToast(t("Avaliação rejeitada com sucesso"));
            onClose();
            fetchRatings(page);
        } catch (error) {
            errorToast(t("Erro ao rejeitar avaliação"));
            console.error(error);
        } finally {
            setRejecting(false);
        }
    };

    const openModal = (mode: "view" | "reject", rating: RatingRow) => {
        setModalMode(mode);
        setSelectedRating(rating);
        onOpen();
    };

    const tabs: { key: FilterTab; label: string }[] = [
        { key: "all", label: "Todas" },
        { key: "driver", label: "Motoristas" },
        { key: "rider", label: "Passageiros" },
        { key: "reported", label: "Denunciadas" },
    ];

    const renderModalContent = () => {
        if (modalMode === "view" && selectedRating) {
            return (
                <>
                    <ModalHeader>{t("Detalhes da Avaliação")}</ModalHeader>
                    <ModalBody>
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm font-semibold text-gray-500">{t("Corrida")}</p>
                                <p className="text-lg">#{selectedRating.rideId}</p>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-500">{t("Avaliador")}</p>
                                <p className="text-lg">
                                    {getRaterName(selectedRating)}{" "}
                                    <span className="text-sm text-gray-400">
                                        ({selectedRating.raterType === "driver" ? "Motorista" : "Passageiro"})
                                    </span>
                                </p>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-500">{t("Avaliado")}</p>
                                <p className="text-lg">
                                    {getRatedName(selectedRating)}{" "}
                                    <span className="text-sm text-gray-400">
                                        ({selectedRating.ratedType === "driver" ? "Motorista" : "Passageiro"})
                                    </span>
                                </p>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-500">{t("Nota")}</p>
                                <div className="flex items-center gap-2">
                                    <StarDisplay stars={selectedRating.stars} />
                                    <span className="text-lg font-bold">{selectedRating.stars}/5</span>
                                </div>
                            </div>
                            {selectedRating.comment && (
                                <div>
                                    <p className="text-sm font-semibold text-gray-500">{t("Comentário")}</p>
                                    <p className="text-lg">{selectedRating.comment}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-sm font-semibold text-gray-500">{t("Data")}</p>
                                <p className="text-lg">
                                    {new Date(selectedRating.createdAt).toLocaleString("pt-BR")}
                                </p>
                            </div>
                            {selectedRating.isReported && (
                                <div className="mt-2 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                                    <p className="text-sm font-bold text-red-400">⚠ {t("Denunciada")}</p>
                                    {selectedRating.reportReason && (
                                        <p className="text-sm text-red-300 mt-1">
                                            {t("Motivo")}: {selectedRating.reportReason}
                                        </p>
                                    )}
                                    {selectedRating.reportComment && (
                                        <p className="text-sm text-red-300 mt-1">
                                            {selectedRating.reportComment}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" onClick={onClose}>
                            {t("Fechar")}
                        </Button>
                        <Button
                            colorScheme="red"
                            ml={3}
                            onClick={() => {
                                onDisclosureClose();
                                setTimeout(() => openModal("reject", selectedRating), 150);
                            }}
                        >
                            {t("Rejeitar")}
                        </Button>
                    </ModalFooter>
                </>
            );
        }

        if (modalMode === "reject" && selectedRating) {
            return (
                <>
                    <ModalHeader>{t("Confirmar Rejeição")}</ModalHeader>
                    <ModalBody>
                        <p>
                            {t("Deseja realmente rejeitar a avaliação")} <strong>#{selectedRating.id}</strong>?
                        </p>
                        <p className="text-sm text-gray-400 mt-2">
                            {t("A avaliação será removida e não será mais visível para os usuários.")}
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            {t("Cancelar")}
                        </Button>
                        <Button
                            colorScheme="red"
                            onClick={handleReject}
                            isLoading={rejecting}
                            loadingText={t("Rejeitando...") as string}
                        >
                            {t("Rejeitar")}
                        </Button>
                    </ModalFooter>
                </>
            );
        }

        return null;
    };

    return (
        <div>
            <Navbar flag={false} brandText={t("Avaliações")} />

            {loading ? (
                <Loader />
            ) : (
                <div className="mt-4 px-4">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-white">
                            {t("Gestão de Avaliações")}
                            <span className="ml-2 text-sm font-normal text-gray-400">
                                ({totalCount} {t("total")})
                            </span>
                        </h2>
                    </div>

                    {/* Filter Tabs */}
                    <div className="flex gap-2 mb-4">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.key
                                    ? "bg-brand-500 text-white shadow-lg shadow-brand-500/30"
                                    : "bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Ratings Table */}
                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden shadow-lg">
                        <table className="w-full">
                            <thead className="bg-navy-900/50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-300">
                                        ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-300">
                                        {t("Corrida")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-300">
                                        {t("Avaliador")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-300">
                                        {t("Avaliado")}
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-300">
                                        {t("Nota")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-300">
                                        {t("Comentário")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-300">
                                        {t("Data")}
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-300">
                                        {t("Status")}
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-300">
                                        {t("Ações")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {ratings.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                                            {t("Nenhuma avaliação encontrada")}
                                        </td>
                                    </tr>
                                ) : (
                                    ratings.map((rating) => (
                                        <tr
                                            key={rating.id}
                                            className="border-t border-white/5 hover:bg-white/5 transition-colors"
                                        >
                                            <td className="px-4 py-3 text-sm text-gray-300">
                                                #{rating.id}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-300">
                                                #{rating.rideId}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center">
                                                    <div
                                                        className={`w-7 h-7 rounded-full flex items-center justify-center mr-2 text-white font-bold text-xs ${rating.raterType === "driver"
                                                            ? "bg-blue-600"
                                                            : "bg-green-600"
                                                            }`}
                                                    >
                                                        {rating.raterType === "driver" ? "M" : "P"}
                                                    </div>
                                                    <div>
                                                        <span className="text-sm font-medium text-white">
                                                            {getRaterName(rating)}
                                                        </span>
                                                        <span className="block text-xs text-gray-500">
                                                            {rating.raterType === "driver"
                                                                ? "Motorista"
                                                                : "Passageiro"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center">
                                                    <div
                                                        className={`w-7 h-7 rounded-full flex items-center justify-center mr-2 text-white font-bold text-xs ${rating.ratedType === "driver"
                                                            ? "bg-blue-600"
                                                            : "bg-green-600"
                                                            }`}
                                                    >
                                                        {rating.ratedType === "driver" ? "M" : "P"}
                                                    </div>
                                                    <div>
                                                        <span className="text-sm font-medium text-white">
                                                            {getRatedName(rating)}
                                                        </span>
                                                        <span className="block text-xs text-gray-500">
                                                            {rating.ratedType === "driver"
                                                                ? "Motorista"
                                                                : "Passageiro"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <StarDisplay stars={rating.stars} />
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-300 max-w-[200px] truncate">
                                                {rating.comment || "-"}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-400">
                                                {new Date(rating.createdAt).toLocaleDateString("pt-BR")}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {rating.isReported ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-900/30 text-red-400 rounded-full text-xs font-medium">
                                                        <MdFlag size={12} /> Denunciada
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-900/30 text-green-400 rounded-full text-xs font-medium">
                                                        ✓ Normal
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => openModal("view", rating)}
                                                        className="p-2 text-blue-400 hover:bg-blue-900/20 rounded-lg transition-colors"
                                                        title={t("Visualizar") as string}
                                                    >
                                                        <MdVisibility size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => openModal("reject", rating)}
                                                        className="p-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title={t("Rejeitar") as string}
                                                    >
                                                        <MdDelete size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-3 mt-4 mb-6">
                            <button
                                onClick={() => fetchRatings(page - 1)}
                                disabled={page <= 1}
                                className="px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-gray-300 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                ← {t("Anterior")}
                            </button>
                            <span className="text-sm text-gray-400">
                                {t("Página")} {page} {t("de")} {totalPages}
                            </span>
                            <button
                                onClick={() => fetchRatings(page + 1)}
                                disabled={page >= totalPages}
                                className="px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-gray-300 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                {t("Próximo")} →
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Modal */}
            <ChakraProvider>
                <Modal isOpen={isOpen} onClose={onClose} isCentered size={modalMode === "reject" ? "sm" : "lg"}>
                    <ModalOverlay />
                    <ModalContent>
                        {renderModalContent()}
                    </ModalContent>
                </Modal>
            </ChakraProvider>
        </div>
    );
};

export default Ratings;
